import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import GusService from '../api_services/gusService.js';
import ApiService from '../api_services/apiService.jsx';
import { ReactComponent as UserEditIcon } from '../icons/user-edit.svg';
import './ProfileEditModal.css';

const ProfileModal = ({ onClose }) => {
    const userEmail = useSelector((state) => state.tokens.userEmail);
    const [step, setStep] = useState(1);
    const [isLoadingGUS, setIsLoadingGUS] = useState(false);
    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    const [formData, setFormData] = useState({
        nip: '',
        companyName: '',
        street: '',
        postalCode: '',
        city: '',
        contactEmail: userEmail || '',
        description: '',
        socialLink: '',
        siteLink: '',
        contactPhoneNo: '',
    });

    useEffect(() => {
        const loadProfile = async () => {
            setIsLoadingProfile(true);
            try {
                const response = await ApiService.getOrganiserProfile();
                const profile = Array.isArray(response) ? response[0] : response;

                if (!profile) {
                    return;
                }

                setFormData((prev) => ({
                    ...prev,
                    nip: profile.nip || '',
                    companyName: profile.companyName || '',
                    street: profile.street || '',
                    postalCode: profile.postalCode || '',
                    city: profile.city || '',
                    contactEmail: profile.contactEmail || prev.contactEmail,
                    description: profile.description || '',
                    socialLink: profile.socialLink || '',
                    siteLink: profile.siteLink || '',
                    contactPhoneNo: profile.contactPhone || '',
                }));
            } catch (error) {
                console.error('Błąd pobierania profilu organizatora:', error);
            } finally {
                setIsLoadingProfile(false);
            }
        };

        loadProfile();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const isNipValid = formData.nip.replace(/[^0-9]/g, '').length === 10;

    const connectWithGUS = async () => {
        const cleanNip = formData.nip.replace(/[^0-9]/g, '');
        if (cleanNip.length !== 10) {
            alert('Podaj poprawny 10-cyfrowy numer NIP');
            return;
        }

        setIsLoadingGUS(true);
        try {
            const companyData = await GusService.fetchFirmaByNip(cleanNip);
            setFormData((prev) => ({
                ...prev,
                companyName: companyData.nazwa || prev.companyName,
                street: companyData.adres || prev.street,
                postalCode: companyData.kodPocztowy || prev.postalCode,
                city: companyData.miasto || prev.city,
            }));
        } catch (error) {
            alert(error.message || 'Nie udało się pobrać danych z GUS.');
        } finally {
            setIsLoadingGUS(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (step === 1) {
            if (!formData.companyName || !formData.contactEmail) {
                alert('Wypełnij wymagane pola');
                return;
            }
            setStep(2);
            return;
        }

        if (!formData.description) {
            alert('Opisz swoją działalność');
            return;
        }

        // Aktualizacja API profilu organizatora nie jest jeszcze dostępna po stronie panelu.
        onClose();
    };

    return (
        <div className="profile-edit-overlay" onClick={onClose}>
            <div className="profile-edit-panel" onClick={(e) => e.stopPropagation()}>
                <div className="profile-edit-header">
                    <h2 className="profile-edit-title">
                        <UserEditIcon className="profile-edit-title-icon" />
                        Edytuj profil organizatora
                    </h2>
                    <button className="profile-edit-close" type="button" onClick={onClose}>&times;</button>
                </div>

                <div className="profile-edit-stepper-shell">
                    <div className="profile-edit-stepper-visual">
                        <div className="profile-edit-stepper-item">
                            <div className={`profile-edit-step-box ${step === 1 ? 'active' : ''}`}>1</div>
                            <div className={`profile-edit-step-label ${step === 1 ? 'active' : ''}`}>Dane organizatora</div>
                        </div>
                        <div className="profile-edit-step-divider">&raquo;&raquo;</div>
                        <div className="profile-edit-stepper-item">
                            <div className={`profile-edit-step-box ${step === 2 ? 'active' : ''}`}>2</div>
                            <div className={`profile-edit-step-label ${step === 2 ? 'active' : ''}`}>Opis i linki</div>
                        </div>
                    </div>
                </div>

                <p className="profile-edit-subtitle">Uzupełnij dane organizatora</p>

                <form className="profile-edit-form" onSubmit={handleSubmit}>
                    {step === 1 && (
                        <>
                            <div className="profile-edit-group">
                                <label htmlFor="nip">NIP (jeśli dotyczy)</label>
                                <div className="profile-edit-input-with-button">
                                    <input
                                        type="text"
                                        id="nip"
                                        name="nip"
                                        placeholder="0000000000"
                                        value={formData.nip}
                                        onChange={handleInputChange}
                                        maxLength={10}
                                    />
                                    <button
                                        type="button"
                                        className="profile-edit-gus-btn"
                                        onClick={connectWithGUS}
                                        disabled={isLoadingGUS || !isNipValid}
                                    >
                                        {isLoadingGUS ? 'Pobieranie...' : 'Pobierz dane z GUS'}
                                    </button>
                                </div>
                            </div>

                            <div className="profile-edit-group">
                                <label htmlFor="companyName">Nazwa organizatora *</label>
                                <input
                                    type="text"
                                    id="companyName"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="profile-edit-group">
                                <label htmlFor="street">Ulica</label>
                                <input
                                    type="text"
                                    id="street"
                                    name="street"
                                    value={formData.street}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="profile-edit-row">
                                <div className="profile-edit-group half">
                                    <label htmlFor="postalCode">Kod pocztowy</label>
                                    <input
                                        type="text"
                                        id="postalCode"
                                        name="postalCode"
                                        placeholder="00-000"
                                        value={formData.postalCode}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="profile-edit-group half">
                                    <label htmlFor="city">Miasto</label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="profile-edit-group">
                                <label htmlFor="contactEmail">Kontakt *</label>
                                <input
                                    type="email"
                                    id="contactEmail"
                                    name="contactEmail"
                                    placeholder="adres e-mail"
                                    value={formData.contactEmail}
                                    onChange={handleInputChange}
                                />
                                <input
                                    type="text"
                                    id="contactPhoneNo"
                                    name="contactPhoneNo"
                                    placeholder="numer telefonu"
                                    value={formData.contactPhoneNo}
                                    onChange={handleInputChange}
                                />
                                <span className="profile-edit-helper">
                                    Numer telefonu wykorzystujemy wyłącznie do kontaktu technicznego i organizacyjnego.
                                </span>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className="profile-edit-group">
                                <label htmlFor="description">Opisz swoją działalność *</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    rows={4}
                                    value={formData.description}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="profile-edit-group">
                                <label htmlFor="socialLink">Link do profilu (np. Facebook, Instagram)</label>
                                <input
                                    type="url"
                                    id="socialLink"
                                    name="socialLink"
                                    placeholder="https://"
                                    value={formData.socialLink}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="profile-edit-group">
                                <label htmlFor="siteLink">Strona WWW</label>
                                <input
                                    type="url"
                                    id="siteLink"
                                    name="siteLink"
                                    placeholder="https://"
                                    value={formData.siteLink}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </>
                    )}

                    <div className="profile-edit-actions">
                        <button type="button" className="profile-edit-btn-light" onClick={onClose}>
                            Anuluj
                        </button>
                        {step === 2 && (
                            <button type="button" className="profile-edit-btn-light" onClick={() => setStep(1)}>
                                Wstecz
                            </button>
                        )}
                        <button type="submit" className="profile-edit-btn-primary" disabled={isLoadingProfile}>
                            {isLoadingProfile ? 'Pobieranie...' : (step === 1 ? 'Dalej' : 'Zapisz zmiany')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProfileModal;

